import {useCallback} from 'react';

import {GetActionFunctionsParams} from 'notebook/structured-tables/util/getActionFunctions';
import {unmergeSingleInput} from 'notebook/tables/excess/ops/TableOps';
import {TokenUpdater} from 'notebook/tables/excess/ops/TokenUpdater';
import toRanges from 'notebook/tables/excess/util/toRanges';
import {Table, TableData, TableDimension, TableOpFunction} from 'notebook/tables/TableConstants';

/** Returns a callback that unmerges the given single inputs in a given table.
 *
 * The callback returns the table with unmerged columns.
 */
export default function useUnmergeBrokenSingleInputsCallback(
  performTableOp: GetActionFunctionsParams<unknown, unknown>['performTableOp']
): (table: Table, brokenSingleInputIndexes: Array<number>) => Table {
  return useCallback(
    (table: Table, brokenSingleInputIndexes: Array<number>) => {
      let unmergedTable = table;
      const tableOpFunc: TableOpFunction = (table, documentName, evaluatedTable) => {
        const continuousRanges = toRanges(brokenSingleInputIndexes);
        let outputTable = table;
        const tokenUpdaters: Array<TokenUpdater> = [];
        for (const [start, end] of continuousRanges.reverse()) {
          const opOutput = unmergeSingleInput(outputTable, documentName, evaluatedTable, start, end);
          outputTable = opOutput.table;
          tokenUpdaters.push(...opOutput.tokenUpdaters);
        }
        unmergedTable = outputTable;
        return {table: outputTable, tokenUpdaters, box: null, dim: TableDimension.COLUMN};
      };
      performTableOp({op: tableOpFunc, options: {remakeSingleInputs: false}});
      return unmergedTable;
    },
    [performTableOp]
  );
}

/**
 * Returns the indexes of columns that are single inputs but have broken merge cells.
 * @param table
 * @returns A list of column indexes (in order)
 */
export function getBrokenSingleInputIndexes(table: TableData): Array<number> {
  const brokenSingleInputIndexes: Array<number> = [];
  for (const [columnIndex, column] of table.columnConstraints.entries()) {
    const {mergeRowSpan} = table.table[0][columnIndex];
    if (
      column?.structuredTableColumn?.isSingleInput &&
      (mergeRowSpan == null || mergeRowSpan !== table.table.length)
    ) {
      brokenSingleInputIndexes.push(columnIndex);
    }
  }
  return brokenSingleInputIndexes;
}
